import * as React from 'react';
import { AlertCircle } from 'lucide-react';
import { useFormContext, type FieldValues } from 'react-hook-form';
import { cn } from '@/lib/utils';

/**
 * Contract: docs/FRONTEND_COMPONENT_INVENTORY.md §5.2.
 */
export interface FormFieldProps {
  name: string;
  label: string;
  description?: string;
  required?: boolean;
  /**
   * A single controlled child; it receives id/aria-* through cloneElement or a
   * render prop.
   *
   * ## Why the TYPE cannot demand that the child accept those props
   * It would be ideal — `children: React.ReactElement<FieldRenderProps>`, and a
   * child that does not declare `aria-invalid`/`aria-describedby` fails to
   * compile — and it DOES NOT WORK, for a reason invisible from reading the
   * type: in `@types/react` (19.2.x, `index.d.ts`), `JSX.Element` is
   * `interface Element extends React.ReactElement<any, any> {}`. That is,
   * `<CompanySelect … />` as an expression has props of type `any`, and `any`
   * satisfies ANY constraint placed on `ReactElement<P>` — the annotation
   * becomes decorative. Checked with `tsc` over a separate file:
   * `const c: React.ReactElement<FieldRenderProps> = <Bad value="x" />` compiles
   * without a single error, with the child declaring
   * `{ id?: string; value: string }` and nothing more. The same with a generic
   * `<P extends …>`: `P` is inferred as `any`.
   *
   * A type that would bite forces the component and its props to be passed
   * separately (`control: React.ComponentType<FieldRenderProps & P>`), because
   * there the component's type is not erased. That changes the call shape of the
   * nine forms that already exist and, while this prop remains available, would
   * not stop the defect being reintroduced by the old route either. That is why
   * the defence is still the check below, which now covers all three
   * attributes.
   */
  children: React.ReactElement | ((f: FieldRenderProps) => React.ReactElement);
  className?: string;
}

export interface FieldRenderProps {
  id: string;
  'aria-invalid': boolean;
  'aria-describedby': string | undefined;
}

/**
 * Clones the child with the field's props (`id`, `aria-invalid`,
 * `aria-describedby`). Every child of the MVP set declares them and forwards
 * them to its DOM node (§3, the inventory's convention 2) — including
 * `DateInput` since its contract (§5.2) learned to combine the external
 * `aria-describedby` with its own instead of replacing it, and since the
 * accessibility wave also `Select` (and with it `CompanySelect` and
 * `DteTypeSelect`) and `FileInput`.
 */
function cloneChildWithFieldProps(
  child: React.ReactElement,
  field: FieldRenderProps,
): React.ReactElement {
  return React.cloneElement(
    child as React.ReactElement<Partial<FieldRenderProps>>,
    field,
  );
}

/**
 * In development, confirms — after mounting — that the DOM really received what
 * was injected through `cloneElement`. A child that does not forward its native
 * props (does not `{...props}` onto the DOM element) produces no React or
 * TypeScript error: the field ends up with no real `aria-invalid` and no real
 * `aria-describedby`, and nobody finds out until somebody tries it with a screen
 * reader. This check turns that into a `console.warn` in development (never in
 * production, and it never throws: it cannot break the render).
 */
function useVerifyChildForwardsFieldProps(
  fieldId: string,
  expectedDescribedBy: string | undefined,
): void {
  React.useEffect(() => {
    if (!import.meta.env.DEV) {
      return;
    }
    const node = document.getElementById(fieldId);
    if (!node) {
      console.warn(
        `FormField: no element with id="${fieldId}" was found in the DOM. ` +
          "FormField's child probably does not forward the `id` prop it received.",
      );
      return;
    }
    // `aria-invalid` is ALWAYS set (it is `false` when there is no error), so
    // its absence from the DOM can only mean the child did not forward it. It
    // used not to be checked: a child could forward `aria-describedby` and lose
    // `aria-invalid`, and the warning never fired.
    if (!node.hasAttribute('aria-invalid')) {
      console.warn(
        `FormField: the element with id="${fieldId}" does not expose the ` +
          '`aria-invalid` FormField injected into it. Check that the child ' +
          'component declares `aria-invalid` in its props and forwards it to ' +
          'the DOM node.',
      );
    }
    if (!expectedDescribedBy) {
      return;
    }
    // A subset, not exact equality: a child like `DateInput` combines its own
    // ids (format hint/error) WITH the ones `FormField` injects — the result in
    // the DOM carries more ids than were injected, and that is fine. What does
    // give away a child that forwards nothing is one of the ids `FormField`
    // passed being missing.
    const actualIds = new Set((node.getAttribute('aria-describedby') ?? '').split(' '));
    const expectedIds = expectedDescribedBy.split(' ');
    const missing = expectedIds.filter((expectedId) => !actualIds.has(expectedId));
    if (missing.length > 0) {
      console.warn(
        `FormField: the element with id="${fieldId}" does not expose the ` +
          '`aria-describedby` FormField injected into it. Check that the child ' +
          'component forwards its native props to the DOM node.',
      );
    }
  });
}

/**
 * Joins label + control + description + Zod error with the right `aria-*`
 * (inventory contract §5.2). The error is read from React Hook Form's context
 * (`useFormContext`), never through a prop: `Form` (§5.2) mounts the
 * `FormProvider` this component consumes.
 *
 * ## Ids
 * They come from `useId()`, not from `name`. Two forms with a `rut` field on the
 * same page share a `name` but never an `id`: `useId()` is unique per mounted
 * component instance, `name` is a domain key that repeats on purpose.
 *
 * ## `aria-describedby`
 * It is a list stable from the first render, not one assembled only when the
 * error appears. The error node (`<p id={errorId}>`) is mounted ALWAYS — empty
 * while there is no error — and its id enters `aria-describedby` from the start
 * if the description or the error can exist at some point. The reason: adding an
 * id to `aria-describedby` in the same render the text it describes appears in
 * is exactly the case where several screen readers do NOT announce it (the
 * reference and the content are both too new at once). By keeping the reference
 * stable and only changing the TEXT of the already-referenced node, the
 * announcement is reliable. When there is no `description`, its id simply does
 * not enter the list (it never points at a node that does not exist); the
 * error's id, by contrast, always corresponds to a real node because that node
 * is always mounted. The order — description first, error second — is the order
 * they appear in the DOM (the description explains the field before typing; the
 * error, what went wrong afterwards) and is the one most screen readers respect
 * when announcing an `aria-describedby` list.
 *
 * ## `required`
 * It is not reflected as `required` or `aria-required` on the control: Zod is
 * the single source of truth for whether a field is mandatory, and the browser
 * (with the native `required` attribute) would validate with ITS own message
 * before Zod ran its own — two messages for the same problem, and not
 * necessarily identical. That is why `FieldRenderProps` (the contract) does not
 * even declare `aria-required`. What `required` does do: it adds a visual
 * asterisk beside the label WITH an accessible equivalent
 * (`<span className="sr-only">, obligatorio</span>`) — an asterisk alone says
 * nothing to whoever cannot see it.
 *
 * ## The two child shapes
 * `cloneElement` (a single `React.ReactElement`) and the render prop
 * (`(f: FieldRenderProps) => React.ReactElement`) produce the same
 * `FieldRenderProps`: both go through the same `fieldProps` object, computed
 * once. There are no asymmetries by child type: every child of the MVP set —
 * `DateInput` included — forwards `id`/`aria-invalid`/`aria-describedby` to its
 * DOM node.
 */
export function FormField({
  name,
  label,
  description,
  required,
  children,
  className,
}: FormFieldProps): React.JSX.Element {
  const reactId = React.useId();
  const fieldId = `${reactId}-field`;
  const descriptionId = `${reactId}-description`;
  const errorId = `${reactId}-error`;

  const { getFieldState, formState } = useFormContext<FieldValues>();
  const { error } = getFieldState(name, formState);
  const errorMessage = typeof error?.message === 'string' ? error.message : undefined;
  const hasError = Boolean(errorMessage);

  const describedByIds = [
    description ? descriptionId : null,
    errorId,
  ].filter((id): id is string => id !== null);
  const describedBy = describedByIds.length > 0 ? describedByIds.join(' ') : undefined;

  const fieldProps: FieldRenderProps = {
    id: fieldId,
    'aria-invalid': hasError,
    'aria-describedby': describedBy,
  };

  useVerifyChildForwardsFieldProps(fieldId, describedBy);

  return (
    <div className={cn('flex flex-col gap-stack-sm', className)}>
      <label htmlFor={fieldId} className="text-body-s font-medium text-fg">
        {label}
        {required ? (
          <>
            <span aria-hidden="true" className="text-danger">
              {' '}
              *
            </span>
            <span className="sr-only">, obligatorio</span>
          </>
        ) : null}
      </label>

      {typeof children === 'function'
        ? children(fieldProps)
        : cloneChildWithFieldProps(children, fieldProps)}

      {description ? (
        <p id={descriptionId} className="text-caption text-fg-muted">
          {description}
        </p>
      ) : null}

      {/*
        Always mounted (see the JSDoc above): empty while there is no error, with
        icon + text when there is one. `role="alert"` is implicitly
        `aria-live="assertive"`; an explicit `aria-live` is added as well because
        some screen readers do not apply the role as a live region until it
        ALREADY has content at the moment it mounts — with `aria-live` set from
        the start, the empty node is already a live region before the first error
        arrives.
      */}
      <p
        id={errorId}
        role="alert"
        aria-live="assertive"
        className={cn(
          'flex items-center gap-stack-sm text-caption text-danger',
          !hasError && 'sr-only',
        )}
      >
        {hasError ? (
          <>
            <AlertCircle aria-hidden="true" className="size-icon-sm shrink-0" />
            {errorMessage}
          </>
        ) : null}
      </p>
    </div>
  );
}
